import { useState } from "react";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import { playNow } from "@/audio/piano";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

const SET_A = ["A", "C", "G", "D"];
const SET_D = ["D", "F", "C", "G"];
const SET_E = ["E", "G", "D", "A"];
const SET_B = ["B", "D", "A", "E"];

const NAMED_SETS: { label: string; notes: string[] }[] = [
  { label: "Start on A", notes: SET_A },
  { label: "Start on D", notes: SET_D },
  { label: "Start on E", notes: SET_E },
  { label: "Start on B", notes: SET_B },
];

// Pinky-thumb-thumb-pinky across both hands, 4 hits per note.
const FINGERS = ["L pinky", "L thumb", "R thumb", "R pinky"];

const PITCH_LH: Record<string, string> = {
  C: "C3", D: "D3", E: "E3", F: "F3", G: "G3", A: "A3", B: "B3",
};
const PITCH_RH: Record<string, string> = {
  C: "C4", D: "D4", E: "E4", F: "F4", G: "G4", A: "A4", B: "B4",
};

export default function PianoFoundation3() {
  const { lesson, course, accent } = findLesson("piano-foundation", "3");
  const [setIdx, setSetIdx] = useState(0);
  const [doublePulse, setDoublePulse] = useState(false);
  const [running, setRunning] = useState(false);
  const [activeCell, setActiveCell] = useState<number | null>(null);

  const currentSet = NAMED_SETS[setIdx];

  const playDrill = () => {
    if (running) return;
    setRunning(true);
    const interval = doublePulse ? 240 : 480; // ms per hit
    const hits: { note: string; finger: number }[] = [];
    for (const note of currentSet.notes) {
      for (let f = 0; f < 4; f++) hits.push({ note, finger: f });
    }
    hits.forEach((h, i) => {
      setTimeout(() => {
        setActiveCell(i);
        const pitch = h.finger < 2 ? PITCH_LH[h.note] : PITCH_RH[h.note];
        void playNow(pitch, "8n", 0.7);
      }, i * interval + 30);
    });
    setTimeout(() => {
      setActiveCell(null);
      setRunning(false);
    }, hits.length * interval + 600);
  };

  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Pick four white notes — the first set is <strong>A, C, G, D</strong> — and play each one four times before moving on.</Highlight>
        <Highlight>Fingering is fixed across both hands: <strong>left pinky, left thumb, right thumb, right pinky</strong>. Same four fingers for every note.</Highlight>
        <Highlight>Lock to a steady pulse — bob your head with the beat. The shifting speed between notes must equal the playing speed.</Highlight>
        <Highlight>Once the pulse is comfortable, <strong>double it</strong> — two notes for every head bob. That's eighth notes.</Highlight>
        <Highlight>Sing along to "Boulevard of Broken Dreams" (Green Day) or "Radioactive" (Imagine Dragons) — both fit the four-note loop beautifully.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The point" title="Find the note. Don't count to it." accent={accent.hex}>
        <p>
          Most beginner books teach you C and then ask you to count up. Want G? Go 1-2-3-4-5
          steps from C. This approach is "absolutely useless" — it brands every
          non-C note as a "tough note", and it'll haunt you for years.
        </p>

        <Pull>
          In music, we are lucky enough to just have 12 notes to deal with across the
          entire spectrum. So I don't think learning 12 notes is that much of a big
          deal.
        </Pull>

        <KeyIdea>
          Goal of this drill: someone says "play A" — bang, you play it. Someone says "play
          D" — bang. No reference, no counting, no climbing up from C. Just the note.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The exercise" title="Four notes · four hits each · pinky-thumb-thumb-pinky" accent={accent.hex}>
        <p>
          The basic drill: pick four white notes. Play each one <strong>four times</strong>{" "}
          before moving on. The fingering is the same across all four notes — left pinky, left
          thumb, right thumb, right pinky. So one note's worth of the drill sounds like:
        </p>
        <p className="font-mono text-[14px] text-amber-300">
          A · A · A · A — and the four fingers travel across both hands.
        </p>
        <p>
          Then move straight to the next note in the set, with no pause:
        </p>
        <p className="font-mono text-[14px] text-amber-300">
          A A A A · C C C C · G G G G · D D D D · repeat.
        </p>

        <Tip>
          The shifting speed has to equal the playing speed. While you're hitting the third or
          fourth A, your brain should already be thinking <em>C</em> — and your finger should
          already be hovering over it. If you stop to find the next note, the pulse breaks and
          the drill loses its value.
        </Tip>
      </Insight>

      <Insight eyebrow="Try it now" title="Play the four-note drill at pulse or double pulse" accent={accent.hex}>
        <div className="rounded-2xl border border-nsm-border bg-nsm-card/40 p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {NAMED_SETS.map((s, i) => (
              <button
                key={s.label}
                onClick={() => setSetIdx(i)}
                className={i === setIdx ? "nsm-btn-primary" : "nsm-btn-ghost"}
              >
                {s.label} · {s.notes.join("-")}
              </button>
            ))}
          </div>
          <div className="mb-3 flex items-center gap-2">
            <button
              onClick={() => setDoublePulse(false)}
              className={!doublePulse ? "nsm-btn-primary" : "nsm-btn-ghost"}
            >
              Pulse
            </button>
            <button
              onClick={() => setDoublePulse(true)}
              className={doublePulse ? "nsm-btn-primary" : "nsm-btn-ghost"}
            >
              Double pulse
            </button>
            <button
              onClick={playDrill}
              disabled={running}
              className="nsm-btn-primary ml-auto disabled:opacity-50"
            >
              {running ? "Playing…" : "Play drill"}
            </button>
          </div>

          {/* The 16-cell grid: 4 notes × 4 fingers */}
          <div className="grid grid-cols-4 gap-2">
            {currentSet.notes.map((note, ni) => (
              <div key={note + ni} className="rounded-lg border border-nsm-border bg-zinc-900/50 p-2">
                <div className="mb-2 text-center font-display text-lg font-bold text-amber-300">
                  {note}
                </div>
                <div className="flex justify-between gap-1">
                  {FINGERS.map((f, fi) => {
                    const cellIdx = ni * 4 + fi;
                    const isActive = activeCell === cellIdx;
                    const isLH = fi < 2;
                    return (
                      <div
                        key={f}
                        className={`flex flex-1 flex-col items-center justify-center rounded p-1.5 text-[9px] uppercase tracking-wider transition ${
                          isActive
                            ? "bg-amber-400/30 text-amber-200"
                            : isLH
                            ? "bg-blue-500/10 text-blue-200/80"
                            : "bg-orange-500/10 text-orange-200/80"
                        }`}
                      >
                        <span className="text-[10px] font-bold">{fi === 0 || fi === 3 ? "5" : "1"}</span>
                        <span className="mt-0.5 text-[8px]">{isLH ? "L" : "R"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-3 text-[12px] text-nsm-dim">
            <span className="text-blue-300">Blue cells</span> are left hand · <span className="text-orange-300">orange cells</span> are right hand. The number is the finger (1 = thumb, 5 = pinky). The pattern: pinky · thumb · thumb · pinky on every note.
          </p>
        </div>
      </Insight>

      <Insight eyebrow="Then sing on top" title="Two rock songs that fit the loop" accent={accent.hex}>
        <p>
          Once the four-note loop sits comfortably, sing over it. the two recommended
          tunes — both work because they hover around the same handful of notes:
        </p>
        <ul className="space-y-1 text-[14px]">
          <li>
            <strong>Boulevard of Broken Dreams</strong> · Green Day. The verse melody fits
            cleanly over A, C, G, D.
          </li>
          <li>
            <strong>Radioactive</strong> · Imagine Dragons. A slightly different feel, also
            sits over the same four notes.
          </li>
        </ul>
        <p>
          Singing while you play is harder than playing alone — and that's exactly why
          Practise harder than you'll ever perform; performing
          will then feel easy.
        </p>
      </Insight>

      <Insight eyebrow="More starting notes" title="Cycle through every white note" accent={accent.hex}>
        <p>
          Once you can do A-C-G-D fluently, change the starting note. We cycle through: </p>
        <Steps>
          <Step>Start on D · D · F · C · G</Step>
          <Step>Start on E · pick four notes around E (E, G, D, A)</Step>
          <Step>Start on B · pick four notes around B (B, D, A, E)</Step>
        </Steps>
        <p>
          Doing all four starting points covers every white note multiple times. Pick the
          one that feels most awkward and drill it longest — that's the one that'll close the
          fastest gap in your reflex.
        </p>
      </Insight>

      <Insight title="Reference · the four notes from set A on the keyboard" accent={accent.hex}>
        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "A3", color: "#3b82f6", label: "A · L" },
            { pitch: "C4", color: "#3b82f6", label: "C · L" },
            { pitch: "G3", color: "#3b82f6", label: "G · L" },
            { pitch: "D4", color: "#3b82f6", label: "D · L" },
            { pitch: "A4", color: "#f97316", label: "A · R" },
            { pitch: "C5", color: "#f97316", label: "C · R" },
            { pitch: "G4", color: "#f97316", label: "G · R" },
            { pitch: "D5", color: "#f97316", label: "D · R" },
          ]}
          height={200}
        />
        <p className="prose-nsm text-sm">
          Blue dots = left-hand notes (lower octave). Orange dots = right-hand notes (upper
          octave). The drill walks through A → C → G → D in both hands, four hits per note.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-4"
        label="Lesson 4 · Rhythm Blocks"
        why="With the white notes becoming reflex, we shift from pitch to rhythm — the bars, beats, and notes that organise time on the piano."
      />
    </LessonShell>
  );
}
